import { useState, useCallback, useRef } from "react";
import {
  fetchBuyerRequirements,
  createBuyerRequirement,
  updateBuyerRequirement,
  deleteBuyerRequirement,
} from "../api/buyerRequirementsApi";

// ── useBuyerRequirements ─────────────────────────────────────────────────────
export function useBuyerRequirements() {
  const [data, setData] = useState(null); // { requirements, meta }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const fetch = useCallback(async (filters = {}) => {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();

    setLoading(true);
    setError(null);
    try {
      const result = await fetchBuyerRequirements(filters);
      setData(result);
    } catch (err) {
      if (err.name !== "AbortError") setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback((filters) => fetch(filters), [fetch]);

  return { data, loading, error, fetch, refresh };
}

// ── useCreateBuyerRequirement ────────────────────────────────────────────────
export function useCreateBuyerRequirement() {
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);

  const mutate = useCallback(async (payload) => {
    setStatus("pending");
    setError(null);
    try {
      const result = await createBuyerRequirement(payload);
      setStatus("success");
      return result;
    } catch (err) {
      const msg =
        err.response?.data?.errors || [err.message || "Something went wrong"];
      setError(msg);
      setStatus("error");
      throw msg;
    }
  }, []);

  return {
    mutate,
    status,
    error,
    isPending: status === "pending",
    reset: () => setStatus("idle"),
  };
}

// ── useUpdateBuyerRequirement ────────────────────────────────────────────────
export function useUpdateBuyerRequirement() {
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);

  const mutate = useCallback(async (id, payload) => {
    setStatus("pending");
    setError(null);
    try {
      const result = await updateBuyerRequirement(id, payload);
      setStatus("success");
      return result;
    } catch (err) {
      const msg =
        err.response?.data?.errors || [err.message || "Something went wrong"];
      setError(msg);
      setStatus("error");
      throw msg;
    }
  }, []);

  return {
    mutate,
    status,
    error,
    isPending: status === "pending",
    reset: () => setStatus("idle"),
  };
}

// ── useDeleteBuyerRequirement ────────────────────────────────────────────────
export function useDeleteBuyerRequirement() {
  const [status, setStatus] = useState("idle");

  const mutate = useCallback(async (id) => {
    setStatus("pending");
    try {
      await deleteBuyerRequirement(id);
      setStatus("success");
    } catch (err) {
      setStatus("error");
      throw err;
    }
  }, []);

  return {
    mutate,
    isPending: status === "pending",
    status,
    reset: () => setStatus("idle"),
  };
}
